import { useState, useEffect } from "react";
import { Card, Col, Row, Statistic, message } from "antd";
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  UserOutlined,
  ShoppingCartOutlined,
  HomeOutlined,
  DollarOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import PageTitle from "../components/PageTitle";
import { getDashboardOverview } from "../api";

// 水滴图标（水表用，避免用盒子状图标）
const WaterDropIcon = ({ style = {}, className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    width="1em"
    height="1em"
    style={{ display: "inline-block", verticalAlign: "-0.15em", ...style }}
  >
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
  </svg>
);

// 生成最近 7 天日期标签（固定 MM-DD 格式，与收租趋势聚合 key 一致）
const getLast7DaysLabels = () => {
  const now = new Date();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${m}-${day}`;
  });
};

// 将接口数据聚合成首页所需结构
function buildDashboardData(overview) {
  const { totalUsers, totalOrders, totalHouses, totalDevices, devices = [], bills = [] } = overview || {};
  const electricList = devices.filter((d) => d.device_type === 1);
  const waterList = devices.filter((d) => d.device_type === 2);
  const lockList = devices.filter((d) => d.device_type === 3);
  const electricTotal = electricList.reduce((s, d) => s + (Number(d.total_electricity) || 0), 0);
  const waterTotal = waterList.reduce((s, d) => s + (Number(d.total_water_quantity) || 0), 0);
  const offlineElectric = electricList.filter((d) => d.is_online === 0).length;
  const offlineWater = waterList.filter((d) => d.is_online === 0).length;

  let collected = 0;
  let uncollected = 0;
  bills.forEach((b) => {
    // 兼容后端：admin/bills 返回 totalAmount（驼峰），其他接口可能返回 total_amount / amount
    const amount = Number(b.total_amount ?? b.amount ?? b.totalAmount ?? 0) || 0;
    // 兼容后端：admin/bills 返回 status 为字符串 "已支付"，其他接口可能为数字 1
    const isPaid = b.status === 1 || b.status === "已支付";
    if (isPaid) collected += amount;
    else uncollected += amount;
  });
  const rentTotal = collected + uncollected;
  const collectedPercent = rentTotal > 0 ? (collected / rentTotal) * 100 : 0;

  const labels = getLast7DaysLabels();
  // 收租趋势：按账单创建日期聚合，key 与 labels 统一用 MM-DD（兼容 createTime/create_time、totalAmount/status）
  const rentByDate = {};
  labels.forEach((label) => { rentByDate[label] = { collected: 0, uncollected: 0 }; });
  bills.forEach((b) => {
    const raw = b.createTime ?? b.create_time ?? "";
    if (!raw) return;
    const d = new Date(raw.replace(/-/g, "/"));
    if (Number.isNaN(d.getTime())) return;
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const key = `${m}-${day}`;
    if (!rentByDate[key]) return;
    const amount = Number(b.total_amount ?? b.amount ?? b.totalAmount ?? 0) || 0;
    const isPaid = b.status === 1 || b.status === "已支付";
    if (isPaid) rentByDate[key].collected += amount;
    else rentByDate[key].uncollected += amount;
  });
  const rentTrendData = labels.map((date) => ({
    date,
    collected: Math.round((rentByDate[date]?.collected ?? 0) * 100) / 100,
    uncollected: Math.round((rentByDate[date]?.uncollected ?? 0) * 100) / 100,
  }));

  // 电费/水费收入趋势：从订单列表按日聚合（与收租一致），只统计支付成功的 order_status===1
  const orders = overview?.orders || [];
  const labelSet = new Set(labels);
  const electricByDate = {};
  const waterByDate = {};
  labels.forEach((label) => { electricByDate[label] = 0; waterByDate[label] = 0; });
  orders.forEach((o) => {
    const orderStatus = Number(o.order_status) || o.order_status;
    const paid = orderStatus === 1 || orderStatus === "1";
    if (!paid) return;
    const raw = (o.pay_time && String(o.pay_time).trim()) || (o.payTime && String(o.payTime).trim()) || o.create_time || o.createTime || "";
    if (!raw) return;
    const d = new Date(String(raw).replace(/-/g, "/").replace("T", " "));
    if (Number.isNaN(d.getTime())) return;
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const key = `${m}-${day}`;
    if (!labelSet.has(key)) return;
    const amount = Number(o.order_amount ?? o.orderAmount ?? 0) || 0;
    const orderType = Number(o.order_type) ?? o.order_type;
    if (orderType === 2 || orderType === "2") electricByDate[key] = (electricByDate[key] ?? 0) + amount;
    if (orderType === 3 || orderType === "3") waterByDate[key] = (waterByDate[key] ?? 0) + amount;
  });
  if (process.env.NODE_ENV !== "production" && orders.length > 0) {
    const paid = orders.filter((o) => Number(o.order_status) === 1 || o.order_status === "1");
    const electric = paid.filter((o) => Number(o.order_type) === 2 || o.order_type === "2");
    const water = paid.filter((o) => Number(o.order_type) === 3 || o.order_type === "3");
    console.log("[首页电费水费] 订单总数:", orders.length, "已支付:", paid.length, "电费订单:", electric.length, "水费订单:", water.length, "近7日电费:", electricByDate, "近7日水费:", waterByDate);
  }
  const electricTrendData = labels.map((date) => ({
    date,
    value: Math.round((electricByDate[date] ?? 0) * 100) / 100,
  }));
  const waterTrendData = labels.map((date) => ({
    date,
    value: Math.round((waterByDate[date] ?? 0) * 100) / 100,
  }));

  return {
    totalUsers: totalUsers ?? 0,
    totalOrders: totalOrders ?? 0,
    totalHouses: totalHouses ?? 0,
    totalDevices: totalDevices ?? 0,
    rent: {
      collected: Math.round(collected * 100) / 100,
      uncollected: Math.round(uncollected * 100) / 100,
      total: Math.round(rentTotal * 100) / 100,
      collectedPercent: Math.round(collectedPercent * 10) / 10,
      trendData: rentTrendData,
    },
    electric: {
      monthTotal: Math.round(electricTotal * 100) / 100,
      totalUsage: Math.round(electricTotal * 100) / 100,
      trendData: electricTrendData,
      offlineCount: offlineElectric,
      deviceCount: electricList.length,
    },
    water: {
      monthTotal: Math.round(waterTotal * 100) / 100,
      totalUsage: Math.round(waterTotal * 100) / 100,
      trendData: waterTrendData,
      offlineCount: offlineWater,
      deviceCount: waterList.length,
    },
    lockCount: lockList.length,
  };
}

// 简单的折线图组件（带数值标签）
const SimpleLineChartWithValue = ({ data, color = '#3366FF', height = 150 }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;
  const padding = 30;
  const bottomPadding = 25;
  const chartHeight = height - padding - bottomPadding;
  
  // 使用 viewBox 坐标系统 (0-100)
  const getPoint = (index, value) => {
    const x = data.length > 1 ? (index / (data.length - 1)) * 100 : 50;
    const normalizedValue = range > 0 ? (value - minValue) / range : 0.5;
    const y = 100 - (normalizedValue * 80) - 10; // 留出上下边距
    return { x, y, value };
  };
  
  const points = data.map((item, index) => getPoint(index, item.value));
  const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');
  
  return (
    <div style={{ position: 'relative', height: `${height}px`, padding: `${padding}px 10px ${bottomPadding}px 10px` }}>
      <svg 
        width="100%" 
        height={`${chartHeight}px`} 
        style={{ position: 'absolute', top: padding, left: '10px', right: '10px' }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <polyline
          points={polylinePoints}
          fill="none"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((point, index) => (
          <g key={index}>
            <circle
              cx={point.x}
              cy={point.y}
              r="2"
              fill={color}
              stroke="#fff"
              strokeWidth="0.5"
            />
            <text
              x={point.x}
              y={point.y - 3}
              textAnchor="middle"
              fontSize="3"
              fontWeight="600"
              fill={color}
            >
              ¥{point.value.toLocaleString()}
            </text>
          </g>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', paddingTop: `${chartHeight + 5}px` }}>
        {data.map((item, index) => (
          <div key={index} style={{ fontSize: '10px', color: '#666', textAlign: 'center', flex: 1 }}>
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};


// 折线图组件
const SimpleLineChart = ({ data, color = '#3366FF', height = 150 }) => {
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;
  const padding = 30;
  const bottomPadding = 25;
  const chartHeight = height - padding - bottomPadding;
  
  // 使用 viewBox 坐标系统 (0-100)
  const getPoint = (index, value) => {
    const x = data.length > 1 ? (index / (data.length - 1)) * 100 : 50;
    const normalizedValue = range > 0 ? (value - minValue) / range : 0.5;
    const y = 100 - (normalizedValue * 80) - 10; // 留出上下边距
    return { x, y };
  };
  
  const points = data.map((item, index) => getPoint(index, item.value));
  const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');
  
  return (
    <div style={{ position: 'relative', height: `${height}px`, padding: `${padding}px 10px ${bottomPadding}px 10px` }}>
      <svg 
        width="100%" 
        height={`${chartHeight}px`} 
        style={{ position: 'absolute', top: padding, left: '10px', right: '10px' }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        <polyline
          points={polylinePoints}
          fill="none"
          stroke={color}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {points.map((point, index) => (
          <circle
            key={index}
            cx={point.x}
            cy={point.y}
            r="2"
            fill={color}
            stroke="#fff"
            strokeWidth="0.5"
          />
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', paddingTop: `${chartHeight + 5}px` }}>
        {data.map((item, index) => (
          <div key={index} style={{ fontSize: '10px', color: '#666', textAlign: 'center', flex: 1 }}>
            {item.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    setLoading(true);
    getDashboardOverview()
      .then((overview) => {
        setData(buildDashboardData(overview));
      })
      .catch((err) => {
        console.error("首页数据加载失败：", err);
        message.error("加载数据概览失败，请稍后重试");
        setData(buildDashboardData(null));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const dateStr = now.toLocaleDateString("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", weekday: "long" });
  const timeStr = now.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });

  if (loading || !data) {
    return (
      <div>
        <PageTitle title="数据概览" />
        <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>加载中...</div>
      </div>
    );
  }

  return (
    <div style={{ margin: "-4px -4px 0", padding: "4px" }}>
      <PageTitle title="数据概览" />
      {/* 数据大屏顶栏：与系统风格一致（白底、#3366FF） */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          padding: "12px 20px",
          background: "#ffffff",
          borderRadius: 12,
          boxShadow: "0 2px 12px rgba(51, 102, 255, 0.08)",
          border: "1px solid rgba(51, 102, 255, 0.12)",
        }}
      >
        <span style={{ fontSize: 14, color: "#666" }}>{/* 左侧留空，仅保留右侧时间 */}</span>
        <div style={{ display: "flex", alignItems: "baseline", gap: 16, fontSize: 13, color: "#666" }}>
          <span>{dateStr}</span>
          <span style={{ color: "#3366FF", fontSize: 18, fontWeight: 600 }}>{timeStr}</span>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.05) 0%, rgba(255, 255, 255, 0.95) 100%)" }}>
            <Statistic title="用户总数" value={data.totalUsers} prefix={<UserOutlined style={{ color: "#3366FF", fontSize: "20px" }} />} valueStyle={{ color: "#3366FF", fontWeight: 700, fontSize: "24px" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.05) 0%, rgba(255, 255, 255, 0.95) 100%)" }}>
            <Statistic title="订单总数" value={data.totalOrders} prefix={<ShoppingCartOutlined style={{ color: "#3366FF", fontSize: "20px" }} />} valueStyle={{ color: "#3366FF", fontWeight: 700, fontSize: "24px" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.05) 0%, rgba(255, 255, 255, 0.95) 100%)" }}>
            <Statistic title="房源总数" value={data.totalHouses} prefix={<HomeOutlined style={{ color: "#3366FF", fontSize: "20px" }} />} valueStyle={{ color: "#3366FF", fontWeight: 700, fontSize: "24px" }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.05) 0%, rgba(255, 255, 255, 0.95) 100%)" }}>
            <Statistic title="设备总数" value={data.totalDevices} prefix={<ThunderboltOutlined style={{ color: "#3366FF", fontSize: "20px" }} />} valueStyle={{ color: "#3366FF", fontWeight: 700, fontSize: "24px" }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.08) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(51, 102, 255, 0.2)" }}>
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>电表数量</div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#3366FF" }}>{data.electric.deviceCount}</div>
              <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>台</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(82, 196, 26, 0.08) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(82, 196, 26, 0.2)" }}>
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>水表数量</div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#52c41a" }}>{data.water.deviceCount}</div>
              <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>台</div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card hoverable style={{ background: "linear-gradient(135deg, rgba(148, 148, 148, 0.08) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(148, 148, 148, 0.2)" }}>
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>门锁数量</div>
              <div style={{ fontSize: "24px", fontWeight: 700, color: "#949494" }}>{data.lockCount ?? 0}</div>
              <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>台</div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DollarOutlined style={{ color: "#3366FF", fontSize: "18px" }} />
            <span style={{ fontSize: "16px", fontWeight: 600 }}>收租统计</span>
            <span style={{ fontSize: "12px", color: "#999", fontWeight: 400 }}>（已收=支付成功）</span>
          </div>
        }
        style={{ marginBottom: 20 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(82, 196, 26, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(82, 196, 26, 0.2)" }}>
              <Statistic title="已收金额" value={data.rent.collected} prefix={<DollarOutlined style={{ color: "#52c41a", fontSize: "18px" }} />} valueStyle={{ color: "#52c41a", fontWeight: 700, fontSize: "22px" }} suffix="元" />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(250, 173, 20, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(250, 173, 20, 0.2)" }}>
              <Statistic title="未收金额" value={data.rent.uncollected} prefix={<DollarOutlined style={{ color: "#faad14", fontSize: "18px" }} />} valueStyle={{ color: "#faad14", fontWeight: 700, fontSize: "22px" }} suffix="元" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="收租比例" hoverable>
              <div style={{ height: "200px", position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[{ name: "已收金额", value: data.rent.collected }, { name: "未收金额", value: data.rent.uncollected }]} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value">
                      <Cell key="已收金额" fill="#52c41a" />
                      <Cell key="未收金额" fill="#faad14" />
                    </Pie>
                    <Tooltip formatter={(value) => `¥${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: "#1E293B" }}>{data.rent.collectedPercent.toFixed(1)}%</div>
                  <div style={{ fontSize: "11px", color: "#666", marginTop: "4px" }}>已收比例</div>
                </div>
              </div>
              <div style={{ marginTop: "16px", fontSize: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                  <div style={{ width: "12px", height: "12px", background: "#52c41a", borderRadius: "2px", marginRight: "8px" }} />
                  <span style={{ color: "#666", flex: 1 }}>已收金额</span>
                  <span style={{ fontWeight: 600, color: "#1E293B" }}>¥{data.rent.collected.toLocaleString()}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: "12px", height: "12px", background: "#faad14", borderRadius: "2px", marginRight: "8px" }} />
                  <span style={{ color: "#666", flex: 1 }}>未收金额</span>
                  <span style={{ fontWeight: 600, color: "#1E293B" }}>¥{data.rent.uncollected.toLocaleString()}</span>
                </div>
              </div>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="收租金额趋势" extra={<span style={{ fontSize: "12px", color: "#999", fontWeight: 400 }}>近7日按创建日期</span>} hoverable>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.rent.trendData.map((item) => ({ date: item.date, value: item.collected }))}>
                  <XAxis dataKey="date" style={{ fontSize: "11px" }} />
                  <YAxis style={{ fontSize: "11px" }} tickFormatter={(value) => `¥${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => [`¥${value.toLocaleString()}`, "已收金额"]} labelStyle={{ fontSize: "12px" }} />
                  <Line type="monotone" dataKey="value" stroke="#52c41a" strokeWidth={2.5} dot={{ fill: "#52c41a", r: 5 }} activeDot={{ r: 7 }} label={{ position: "top", formatter: (value) => `¥${value.toLocaleString()}`, style: { fontSize: "10px", fill: "#52c41a", fontWeight: 600 } }} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ marginTop: "16px", padding: "12px", background: "rgba(51, 102, 255, 0.04)", borderRadius: "8px", fontSize: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}><span style={{ color: "#666" }}>总金额：</span><span style={{ fontWeight: 600, color: "#1E293B" }}>¥{data.rent.total.toLocaleString()}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}><span style={{ color: "#666" }}>已收金额：</span><span style={{ fontWeight: 600, color: "#52c41a" }}>¥{data.rent.collected.toLocaleString()}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}><span style={{ color: "#666" }}>未收金额：</span><span style={{ fontWeight: 600, color: "#faad14" }}>¥{data.rent.uncollected.toLocaleString()}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#666" }}>已收比例：</span><span style={{ fontWeight: 600, color: "#3366FF" }}>{data.rent.collectedPercent}%</span></div>
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card title={<div style={{ display: "flex", alignItems: "center", gap: "8px" }}><ThunderboltOutlined style={{ color: "#3366FF", fontSize: "18px" }} /><span style={{ fontSize: "16px", fontWeight: 600 }}>智能电表</span></div>} style={{ marginBottom: 20 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(51, 102, 255, 0.2)" }}>
              <Statistic title="本月累计用电" value={data.electric.monthTotal} prefix={<ThunderboltOutlined style={{ color: "#3366FF", fontSize: "18px" }} />} valueStyle={{ color: "#3366FF", fontWeight: 700, fontSize: "22px" }} suffix="kWh" />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(51, 102, 255, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(51, 102, 255, 0.2)" }}>
              <Statistic title="总用电量" value={data.electric.totalUsage} prefix={<ThunderboltOutlined style={{ color: "#3366FF", fontSize: "18px" }} />} valueStyle={{ color: "#3366FF", fontWeight: 700, fontSize: "22px" }} suffix="kWh" />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(255, 77, 79, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(255, 77, 79, 0.2)" }}>
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>离线电表</div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#ff4d4f" }}>{data.electric.offlineCount}</div>
              <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>台</div>
            </div>
          </Card>
          </Col>
          <Col xs={24}>
            <Card title="电费收入趋势" hoverable>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={data.electric.trendData.map((item) => ({ date: item.date, value: item.value }))}>
                  <XAxis dataKey="date" style={{ fontSize: "11px" }} />
                  <YAxis style={{ fontSize: "11px" }} label={{ value: "元", angle: -90, position: "insideLeft", style: { fontSize: "11px" } }} />
                  <Tooltip formatter={(value) => [`¥${Number(value).toFixed(2)}`, "电费"]} labelStyle={{ fontSize: "12px" }} />
                  <Line type="monotone" dataKey="value" stroke="#3366FF" strokeWidth={2.5} dot={{ fill: "#3366FF", r: 5 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ fontSize: "12px", color: "#999", marginTop: "8px" }}>近7日每日电费实收金额（元），来自已支付电表充值订单。</div>
            </Card>
          </Col>
        </Row>
      </Card>

      <Card title={<div style={{ display: "flex", alignItems: "center", gap: "8px" }}><WaterDropIcon style={{ color: "#52c41a", fontSize: "18px" }} /><span style={{ fontSize: "16px", fontWeight: 600 }}>智能水表</span></div>}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(82, 196, 26, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(82, 196, 26, 0.2)" }}>
              <Statistic title="本月累计用水" value={data.water.monthTotal} prefix={<WaterDropIcon style={{ color: "#52c41a", fontSize: "18px" }} />} valueStyle={{ color: "#52c41a", fontWeight: 700, fontSize: "22px" }} suffix="吨" />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(82, 196, 26, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(82, 196, 26, 0.2)" }}>
              <Statistic title="总用水量" value={data.water.totalUsage} prefix={<WaterDropIcon style={{ color: "#52c41a", fontSize: "18px" }} />} valueStyle={{ color: "#52c41a", fontWeight: 700, fontSize: "22px" }} suffix="吨" />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card hoverable style={{ background: "linear-gradient(135deg, rgba(255, 77, 79, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)", border: "1px solid rgba(255, 77, 79, 0.2)" }}>
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: "14px", color: "#666", marginBottom: "12px" }}>离线水表</div>
              <div style={{ fontSize: "28px", fontWeight: 700, color: "#ff4d4f" }}>{data.water.offlineCount}</div>
              <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>台</div>
            </div>
          </Card>
          </Col>
          <Col xs={24}>
            <Card title="水费收入趋势" hoverable>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={data.water.trendData.map((item) => ({ date: item.date, value: item.value }))}>
                  <XAxis dataKey="date" style={{ fontSize: "11px" }} />
                  <YAxis style={{ fontSize: "11px" }} label={{ value: "元", angle: -90, position: "insideLeft", style: { fontSize: "11px" } }} />
                  <Tooltip formatter={(value) => [`¥${Number(value).toFixed(2)}`, "水费"]} labelStyle={{ fontSize: "12px" }} />
                  <Line type="monotone" dataKey="value" stroke="#52c41a" strokeWidth={2.5} dot={{ fill: "#52c41a", r: 5 }} activeDot={{ r: 7 }} />
                </LineChart>
              </ResponsiveContainer>
              <div style={{ fontSize: "12px", color: "#999", marginTop: "8px" }}>近7日每日水费实收金额（元），来自已支付水表充值订单。</div>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
